import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Users, UserCheck, Shield, Plus, Search, X, Loader2,
  Check, Info, Lock, Save, AlertCircle, RefreshCw,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import ConfirmDeleteModal from '../../../components/common/ConfirmDeleteModal';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { authService, userService, memberApprovalService, roleService, ROLE_OPTIONS } from '..';
import { BRAND_COLORS, SYSTEM_COLORS, NEUTRAL_COLORS } from '../../../constants/brandPalette';

// ── Permission group helpers (mirrored from PermissionsPage) ─────────────────

const PERM_GROUP_LABELS = {
  dashboard: 'Dashboard', projects: 'Projects', team: 'Team',
  resources: 'Resources', training: 'Training', reports: 'Reports',
  tasks: 'Tasks', admin: 'Admin Functions', clients: 'Clients',
  competitions: 'Competitions', marketing: 'Marketing',
  pr: 'Public Relations', graphics: 'Graphics', calendar: 'Calendar',
};

const toTitle = (v = '') =>
  v.replace(/[_.]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2')
   .replace(/\s+/g, ' ').trim().replace(/\b\w/g, c => c.toUpperCase());

const toRoleKey = (role) => {
  if (!role) return '';
  const m = role.description?.match(/default\s+([a-z_]+)\s+role/i);
  if (m?.[1]) return m[1].toLowerCase();
  return (role.name || '').trim().toLowerCase().replace(/\s+/g, '_');
};

const normalizeRole = (role, permIds = []) => {
  const rp = role?.permissions || {};
  const np = {};
  permIds.forEach(id => { np[id] = rp[id] === true; });
  Object.keys(rp).forEach(id => { if (!(id in np)) np[id] = rp[id] === true; });
  return { ...role, key: toRoleKey(role), isSystem: Boolean(role?.is_system ?? role?.isSystem), permissions: np };
};

const buildPermGroups = (permIds) => {
  const grouped = permIds.reduce((acc, id) => {
    const [prefix, suffix] = id.split('.');
    const key = prefix || 'other';
    if (!acc[key]) acc[key] = [];
    acc[key].push({ id, label: toTitle(suffix || id), description: `Allow ${toTitle(suffix || id).toLowerCase()} in ${PERM_GROUP_LABELS[key] || toTitle(key)}.` });
    return acc;
  }, {});
  return Object.keys(grouped)
    .sort((a, b) => (PERM_GROUP_LABELS[a] || toTitle(a)).localeCompare(PERM_GROUP_LABELS[b] || toTitle(b)))
    .map(key => ({ page: PERM_GROUP_LABELS[key] || toTitle(key), permissions: grouped[key].sort((a, b) => a.id.localeCompare(b.id)) }));
};

// ── Shared helpers ────────────────────────────────────────────────────────────

const AUTH_CODE_ROLES = ['admin', 'client'];

const inp = (err) =>
  `w-full px-3 py-2 rounded-xl border text-sm outline-none transition-all focus:ring-2 focus:ring-[#37559e]/20 focus:border-[#37559e] ${
    err ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'
  }`;

const initials = (name = '') =>
  name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'U';

const avatarColor = (name = '') => {
  const palette = [BRAND_COLORS.primary, BRAND_COLORS.accent, '#10b981', '#8b5cf6', '#f59e0b', '#ec4899'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return palette[Math.abs(h) % palette.length];
};

const StatusBadge = ({ status }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
    status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
  }`}>
    {status || 'unknown'}
  </span>
);

const Spinner = () => (
  <div className="flex items-center justify-center gap-3 py-16">
    <Loader2 size={20} className="animate-spin" style={{ color: BRAND_COLORS.primary }} />
    <span className="text-sm text-gray-500">Loading…</span>
  </div>
);

// ── Create User Modal ─────────────────────────────────────────────────────────

const CreateUserModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: '', email: '', password: '', confirm_password: '',
    role: 'community_member', phone: '', auth_code: '',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const needsCode = AUTH_CODE_ROLES.includes(form.role);

  const set = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email';
    if (!form.password) e.password = 'Required';
    else if (form.password.length < 8) e.password = 'Min 8 characters';
    if (form.password !== form.confirm_password) e.confirm_password = 'Does not match';
    if (needsCode && !form.auth_code.trim()) e.auth_code = 'Required for this role';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), email: form.email.trim(), password: form.password, confirm_password: form.confirm_password, role: form.role };
      if (form.phone.trim()) payload.phone = form.phone.trim();
      if (needsCode && form.auth_code.trim()) payload.auth_code = form.auth_code.trim();
      const result = await authService.signup(payload);
      toast.success(result.requires_approval ? `"${form.name}" created — pending approval` : `"${form.name}" created successfully`);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err?.message || err?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="w-full max-w-md flex flex-col max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden"
        style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}>
        <div className="flex items-center justify-between flex-shrink-0 px-5 py-4"
          style={{ backgroundColor: BRAND_COLORS.primary }}>
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-8 h-8 rounded-xl bg-white/20">
              <Users size={14} className="text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Create New User</p>
              <p className="text-[10px] text-white/70">Register a new system account</p>
            </div>
          </div>
          <button onClick={onClose} className="flex items-center justify-center rounded-lg w-7 h-7 bg-white/10 hover:bg-white/20">
            <X size={14} className="text-white" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Full Name <span className="text-red-400">*</span></label>
              <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="John Doe" className={inp(errors.name)} />
              {errors.name && <p className="text-red-500 text-[10px] mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Phone</label>
              <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+255 7XX…" className={inp(false)} />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Email <span className="text-red-400">*</span></label>
            <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="user@example.com" className={inp(errors.email)} />
            {errors.email && <p className="text-red-500 text-[10px] mt-1">{errors.email}</p>}
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Role <span className="text-red-400">*</span></label>
            <select value={form.role} onChange={e => set('role', e.target.value)} className={inp(false)}>
              {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            {needsCode && <p className="text-[10px] text-amber-600 mt-1">This role requires an authorization code.</p>}
          </div>
          {needsCode && (
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Auth Code <span className="text-red-400">*</span></label>
              <input value={form.auth_code} onChange={e => set('auth_code', e.target.value)} placeholder="Authorization code" className={inp(errors.auth_code)} />
              {errors.auth_code && <p className="text-red-500 text-[10px] mt-1">{errors.auth_code}</p>}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Password <span className="text-red-400">*</span></label>
              <input type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="Min 8 chars" className={inp(errors.password)} />
              {errors.password && <p className="text-red-500 text-[10px] mt-1">{errors.password}</p>}
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Confirm <span className="text-red-400">*</span></label>
              <input type="password" value={form.confirm_password} onChange={e => set('confirm_password', e.target.value)} placeholder="Repeat password" className={inp(errors.confirm_password)} />
              {errors.confirm_password && <p className="text-red-500 text-[10px] mt-1">{errors.confirm_password}</p>}
            </div>
          </div>
        </form>

        <div className="flex justify-end flex-shrink-0 gap-2 px-5 py-4 border-t"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 rounded-xl hover:bg-gray-100">Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: BRAND_COLORS.primary }}>
            {saving ? <><Loader2 size={13} className="animate-spin" /> Creating…</> : <><Plus size={13} /> Create User</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Edit Permissions Modal ────────────────────────────────────────────────────

const EditPermissionsModal = ({ role, permissionsByPage, usersForRole, onClose, onSaved }) => {
  const [edited, setEdited] = useState(role.permissions || {});
  const [saving, setSaving] = useState(false);

  const toggle = (id, val) => setEdited(prev => ({ ...prev, [id]: val }));
  const togglePage = (perms, val) => setEdited(prev => {
    const u = {};
    perms.forEach(p => { u[p.id] = val; });
    return { ...prev, ...u };
  });

  const isFull = (perms) => perms.every(p => edited[p.id] === true);
  const isPartial = (perms) => {
    const n = perms.filter(p => edited[p.id] === true).length;
    return n > 0 && n < perms.length;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await roleService.updateRolePermissions(role.key || role.id, edited);
      toast.success(`Permissions updated for ${role.name}`);
      onSaved(role.id, edited);
      onClose();
    } catch {
      toast.error('Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="w-full max-w-3xl flex flex-col max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden bg-white">
        <div className="flex items-center justify-between flex-shrink-0 px-6 py-4"
          style={{ backgroundColor: BRAND_COLORS.primary }}>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-xl bg-white/20">
              <Shield size={15} className="text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Edit Permissions — {role.name}</p>
              <p className="text-[10px] text-white/70">{usersForRole} user{usersForRole !== 1 ? 's' : ''} with this role</p>
            </div>
          </div>
          <button onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-xl bg-white/10 hover:bg-white/20">
            <X size={14} className="text-white" />
          </button>
        </div>

        <div className="flex-1 p-5 overflow-y-auto">
          {role.isSystem && role.key === 'admin' ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12">
              <div className="flex items-center justify-center w-14 h-14 rounded-2xl"
                style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
                <Lock size={22} style={{ color: SYSTEM_COLORS.textSoft }} />
              </div>
              <p className="text-sm text-center" style={{ color: SYSTEM_COLORS.textMuted }}>
                Admin role has all system permissions automatically.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {permissionsByPage.map(page => {
                const full = isFull(page.permissions);
                const partial = isPartial(page.permissions);
                return (
                  <div key={page.page} className="overflow-hidden border rounded-xl"
                    style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                    <div className="flex items-center justify-between px-4 py-2.5"
                      style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                      <div className="flex items-center gap-2">
                        {full ? (
                          <div className="flex items-center justify-center w-4 h-4 bg-green-500 rounded-full">
                            <Check size={9} className="text-white" />
                          </div>
                        ) : partial ? (
                          <div className="flex items-center justify-center w-4 h-4 border-2 rounded-full"
                            style={{ borderColor: BRAND_COLORS.accent }}>
                            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: BRAND_COLORS.accent }} />
                          </div>
                        ) : (
                          <div className="w-4 h-4 border-2 border-gray-300 rounded-full" />
                        )}
                        <span className="text-xs font-semibold" style={{ color: NEUTRAL_COLORS.black }}>{page.page}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <button type="button" onClick={() => togglePage(page.permissions, true)}
                          className="text-[10px] font-medium" style={{ color: BRAND_COLORS.primary }}>All</button>
                        <button type="button" onClick={() => togglePage(page.permissions, false)}
                          className="text-[10px] font-medium text-gray-400 hover:text-gray-600">None</button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2">
                      {page.permissions.map(perm => (
                        <label key={perm.id} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50 border-t"
                          style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                          <input type="checkbox"
                            checked={edited[perm.id] === true}
                            onChange={e => toggle(perm.id, e.target.checked)}
                            className="flex-shrink-0 w-4 h-4 border-gray-300 rounded"
                            style={{ accentColor: BRAND_COLORS.primary }} />
                          <div>
                            <p className="text-xs font-medium text-gray-700">{perm.label}</p>
                            <p className="text-[10px] text-gray-400 leading-relaxed">{perm.description}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end flex-shrink-0 gap-2 px-6 py-4 border-t"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 rounded-xl hover:bg-gray-100">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: BRAND_COLORS.primary }}>
            {saving ? <><Loader2 size={13} className="animate-spin" /> Saving…</> : <><Save size={13} /> Save Changes</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Tab 1 — User Management ───────────────────────────────────────────────────

const UsersTab = ({ isAdmin }) => {
  const { user: currentUser } = useApiAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editedRoles, setEditedRoles] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);

  const isSelf = (u) => String(u.id) === String(currentUser?.id);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, rRes] = await Promise.allSettled([userService.getAllUsers(), roleService.getRoles()]);
      if (uRes.status === 'fulfilled') {
        const all = Array.isArray(uRes.value) ? uRes.value : (uRes.value?.users || []);
        setUsers(all);
      } else toast.error('Failed to load users');
      if (rRes.status === 'fulfilled') {
        const all = Array.isArray(rRes.value) ? rRes.value : (rRes.value?.roles || []);
        setRoles(all);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    return (!q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)) &&
      (roleFilter === 'all' || u.role === roleFilter);
  });

  const currentRole = (u) => editedRoles[u.id] !== undefined ? editedRoles[u.id] : (u.role || '');
  const isDirty = (u) => editedRoles[u.id] !== undefined && editedRoles[u.id] !== u.role;

  const roleOptions = useMemo(() => {
    if (!roles.length) return ROLE_OPTIONS;
    return ROLE_OPTIONS.map(option => {
      const match = roles.find(r => {
        const roleKey = String(r.key || r.value || '').toLowerCase();
        return roleKey === option.value.toLowerCase() || String(r.name || '').toLowerCase() === option.label.toLowerCase();
      });
      return {
        ...option,
        id: match?.id ?? option.value,
      };
    });
  }, [roles]);

  const handleSaveRole = async (u) => {
    if (isSelf(u)) return;
    const newRole = editedRoles[u.id];
    if (!newRole || newRole === u.role) return;
    setSavingId(u.id);
    try {
      await userService.updateUserRole(u.id, newRole);
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, role: newRole } : x));
      setEditedRoles(prev => { const n = { ...prev }; delete n[u.id]; return n; });
      toast.success(`Role updated for ${u.name}`);
    } catch { toast.error('Failed to update role'); }
    finally { setSavingId(null); }
  };

  const roleSelect = (u) => (
    <div className="flex items-center gap-2">
      <select
        value={currentRole(u)}
        onChange={e => setEditedRoles(prev => ({ ...prev, [u.id]: e.target.value }))}
        disabled={isSelf(u)}
        className="flex-1 px-2 py-1.5 rounded-lg border text-xs outline-none focus:ring-2 focus:ring-[#37559e]/20 focus:border-[#37559e] transition-colors disabled:bg-gray-100 disabled:text-gray-400"
        style={{ borderColor: isSelf(u) ? SYSTEM_COLORS.border : (isDirty(u) ? BRAND_COLORS.primary : SYSTEM_COLORS.border) }}
      >
        <option value="">— Select Role —</option>
        {roleOptions.map(r => (
          <option key={r.id || r.value} value={r.value}>{r.label}</option>
        ))}
      </select>
      {isSelf(u) && (
        <span className="text-[10px] text-gray-500">You cannot change your own role.</span>
      )}
      {!isSelf(u) && isDirty(u) && (
        <button onClick={() => handleSaveRole(u)} disabled={savingId === u.id}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50 whitespace-nowrap"
          style={{ backgroundColor: BRAND_COLORS.primary }}>
          {savingId === u.id ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />}
          Save
        </button>
      )}
    </div>
  );

  if (loading) return <Spinner />;

  const stats = [
    { label: 'Total Users', value: users.length, color: BRAND_COLORS.primary },
    { label: 'Active', value: users.filter(u => u.status === 'active').length, color: SYSTEM_COLORS.success },
    { label: 'Pending', value: users.filter(u => u.status !== 'active').length, color: SYSTEM_COLORS.warning },
  ];

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {stats.map(s => (
          <div key={s.label} className="px-4 py-3 border rounded-xl"
            style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
            <p className="text-xl font-bold sm:text-2xl" style={{ color: s.color }}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="flex items-center flex-1 gap-2 rounded-xl border px-3 py-2.5"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
          <Search size={15} className="flex-shrink-0 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email…"
            className="flex-1 text-sm bg-transparent outline-none" />
          {search && <button onClick={() => setSearch('')}><X size={13} className="text-gray-400 hover:text-gray-600" /></button>}
        </div>
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
          className="sm:w-40 px-3 py-2.5 rounded-xl border text-sm outline-none"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
          <option value="all">All Roles</option>
          {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        {isAdmin && (
          <button onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white whitespace-nowrap shadow-sm hover:opacity-90"
            style={{ backgroundColor: BRAND_COLORS.primary }}>
            <Plus size={15} /> Create User
          </button>
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden overflow-hidden border lg:block rounded-2xl"
        style={{ borderColor: SYSTEM_COLORS.borderSoft, boxShadow: SYSTEM_COLORS.shadowSoft }}>
        <table className="min-w-full" style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}>
          <thead>
            <tr style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
              {['User', 'Email', 'Status', 'Role', ''].map(h => (
                <th key={h} className={`px-5 py-3 text-[10px] font-semibold uppercase tracking-wider text-gray-500 ${h === '' ? 'text-right w-10' : 'text-left'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={5} className="px-5 py-12 text-sm text-center text-gray-400">No users found</td></tr>
            ) : filtered.map((u, i) => (
              <tr key={u.id}
                className="transition-colors hover:bg-gray-50"
                style={{ borderTop: i > 0 ? `1px solid ${SYSTEM_COLORS.borderSoft}` : 'none' }}>
                <td className="px-5 py-3.5 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    {u.photo_url
                      ? <img src={u.photo_url} alt="" className="flex-shrink-0 object-cover w-8 h-8 rounded-full" />
                      : <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 text-xs font-bold text-white rounded-full"
                          style={{ backgroundColor: avatarColor(u.name) }}>{initials(u.name)}</div>}
                    <span className="text-sm font-medium text-gray-900">{u.name || 'Unnamed'}</span>
                  </div>
                </td>
                <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-500">{u.email}</td>
                <td className="px-5 py-3.5 whitespace-nowrap"><StatusBadge status={u.status} /></td>
                <td className="px-5 py-3.5 min-w-[200px]">{roleSelect(u)}</td>
                <td className="px-5 py-3.5" />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="space-y-3 lg:hidden">
        {filtered.length === 0
          ? <div className="py-10 text-sm text-center text-gray-400">No users found</div>
          : filtered.map(u => (
          <div key={u.id} className="p-4 border rounded-2xl"
            style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
            <div className="flex items-center gap-3 mb-3">
              {u.photo_url
                ? <img src={u.photo_url} alt="" className="flex-shrink-0 object-cover rounded-full w-9 h-9" />
                : <div className="flex items-center justify-center flex-shrink-0 text-xs font-bold text-white rounded-full w-9 h-9"
                    style={{ backgroundColor: avatarColor(u.name) }}>{initials(u.name)}</div>}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{u.name || 'Unnamed'}</p>
                <p className="text-xs text-gray-500 truncate">{u.email}</p>
              </div>
              <StatusBadge status={u.status} />
            </div>
            {roleSelect(u)}
          </div>
        ))}
      </div>

      {!loading && users.length > 0 && (
        <p className="text-xs text-gray-400">Showing {filtered.length} of {users.length} users</p>
      )}

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={load} />}
    </div>
  );
};

// ── Tab 2 — Member Approvals ──────────────────────────────────────────────────

const ApprovalsTab = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [rejectTarget, setRejectTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await memberApprovalService.getPendingApprovals();
      setPending(Array.isArray(res) ? res : []);
    } catch { toast.error('Failed to load approvals'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = pending.filter(m =>
    !search ||
    m.name?.toLowerCase().includes(search.toLowerCase()) ||
    m.email?.toLowerCase().includes(search.toLowerCase()) ||
    m.role?.toLowerCase().includes(search.toLowerCase())
  );

  const handleApprove = async (id, name) => {
    try { await memberApprovalService.approveMember(id); toast.success(`${name} approved`); load(); }
    catch { toast.error('Failed to approve'); }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    try { await memberApprovalService.rejectMember(rejectTarget.id); toast.success(`${rejectTarget.name} rejected`); setRejectTarget(null); load(); }
    catch { toast.error('Failed to reject'); }
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex items-center gap-2 rounded-xl border px-3 py-2.5"
        style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
        <Search size={15} className="flex-shrink-0 text-gray-400" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, or role…"
          className="flex-1 text-sm bg-transparent outline-none" />
        {search && <button onClick={() => setSearch('')}><X size={13} className="text-gray-400 hover:text-gray-600" /></button>}
      </div>

      <p className="text-xs font-medium" style={{ color: SYSTEM_COLORS.textMuted }}>
        {filtered.length} pending approval{filtered.length !== 1 ? 's' : ''}
      </p>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <div className="flex items-center justify-center w-14 h-14 rounded-2xl"
            style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}>
            <UserCheck size={22} style={{ color: BRAND_COLORS.primary }} />
          </div>
          <p className="text-sm font-medium text-gray-700">{search ? 'No matches found' : 'No pending approvals'}</p>
          <p className="text-xs text-gray-400">{search ? 'Try a different query' : 'All registrations have been processed'}</p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden border sm:block rounded-2xl"
            style={{ borderColor: SYSTEM_COLORS.borderSoft, boxShadow: SYSTEM_COLORS.shadowSoft }}>
            <table className="min-w-full" style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}>
              <thead>
                <tr style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                  {['Member', 'Email', 'Role', 'Requested', 'Actions'].map(h => (
                    <th key={h} className={`px-5 py-3 text-[10px] font-semibold uppercase tracking-wider text-gray-500 ${h === 'Actions' ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((m, i) => (
                  <tr key={m.id} className="transition-colors hover:bg-gray-50"
                    style={{ borderTop: i > 0 ? `1px solid ${SYSTEM_COLORS.borderSoft}` : 'none' }}>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 text-xs font-bold text-white rounded-full"
                          style={{ backgroundColor: BRAND_COLORS.primary }}>{initials(m.name)}</div>
                        <span className="text-sm font-medium text-gray-900">{m.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-500">{m.email}</td>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                        style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}>{m.role}</span>
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-xs text-gray-400">
                      {m.created_at ? new Date(m.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => handleApprove(m.id, m.name)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                          style={{ backgroundColor: SYSTEM_COLORS.success }}>
                          <UserCheck size={11} /> Approve
                        </button>
                        <button onClick={() => setRejectTarget({ id: m.id, name: m.name })}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                          style={{ backgroundColor: SYSTEM_COLORS.danger }}>
                          <X size={11} /> Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-3 sm:hidden">
            {filtered.map(m => (
              <div key={m.id} className="p-4 border rounded-2xl"
                style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 text-sm font-bold text-white rounded-full"
                    style={{ backgroundColor: BRAND_COLORS.primary }}>{initials(m.name)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{m.name}</p>
                    <p className="text-xs text-gray-500 truncate">{m.email}</p>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
                    style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}>{m.role}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">{m.created_at ? new Date(m.created_at).toLocaleDateString() : 'N/A'}</span>
                  <div className="flex gap-2">
                    <button onClick={() => handleApprove(m.id, m.name)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                      style={{ backgroundColor: SYSTEM_COLORS.success }}>
                      <UserCheck size={11} /> Approve
                    </button>
                    <button onClick={() => setRejectTarget({ id: m.id, name: m.name })}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                      style={{ backgroundColor: SYSTEM_COLORS.danger }}>
                      <X size={11} /> Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <ConfirmDeleteModal
        isOpen={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        onConfirm={handleReject}
        title="Reject Member"
        description={rejectTarget ? `Reject "${rejectTarget.name}"? They will need to register again.` : ''}
      />
    </div>
  );
};

// ── Tab 3 — Permissions (Role Management) ─────────────────────────────────────

const PermissionsTab = () => {
  const [roles, setRoles] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [permCatalog, setPermCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editRole, setEditRole] = useState(null);

  const permsByPage = useMemo(() => buildPermGroups(permCatalog), [permCatalog]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, rRes, uRes] = await Promise.allSettled([
        roleService.getPermissionsCatalog(),
        roleService.getRoles(),
        userService.getAllUsers(),
      ]);
      const perms = Array.isArray(pRes.value?.permissions) ? pRes.value.permissions :
        Array.isArray(pRes.value) ? pRes.value : [];
      setPermCatalog(perms);
      const rolesData = Array.isArray(rRes.value) ? rRes.value : (rRes.value?.roles || []);
      setRoles(rolesData.map(r => normalizeRole(r, perms)));
      const usersData = Array.isArray(uRes.value) ? uRes.value : (uRes.value?.users || []);
      setAllUsers(usersData);
    } catch { toast.error('Failed to load roles'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const usersForRole = (role) => {
    const key = String(role?.key || '').toLowerCase();
    const nameKey = String(role?.name || '').trim().toLowerCase().replace(/\s+/g, '_');
    return allUsers.filter(u => {
      const ur = String(u?.role || '').toLowerCase();
      return ur === key || ur === nameKey || ur === String(role?.id || '').toLowerCase();
    }).length;
  };

  const permCount = (role) => Object.values(role.permissions || {}).filter(Boolean).length;

  const filteredRoles = roles.filter(r =>
    !search || r.name?.toLowerCase().includes(search.toLowerCase()) ||
    r.description?.toLowerCase().includes(search.toLowerCase())
  );

  const handlePermSaved = (roleId, newPerms) =>
    setRoles(prev => prev.map(r => r.id === roleId ? { ...r, permissions: newPerms } : r));

  if (loading && roles.length === 0) return <Spinner />;

  return (
    <div className="space-y-5">
      {/* Info banner */}
      <div className="flex items-start gap-3 px-4 py-3 border-l-4 rounded-xl"
        style={{ borderColor: BRAND_COLORS.accent, backgroundColor: '#fef3ee' }}>
        <Info size={14} className="flex-shrink-0 mt-0.5" style={{ color: BRAND_COLORS.accent }} />
        <p className="text-xs leading-relaxed" style={{ color: SYSTEM_COLORS.textMuted }}>
          Changing role permissions affects all users with that role. Changes take effect immediately.
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2">
        <div className="flex items-center flex-1 gap-2 rounded-xl border px-3 py-2.5"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
          <Search size={15} className="flex-shrink-0 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search roles…"
            className="flex-1 text-sm bg-transparent outline-none" />
          {search && <button onClick={() => setSearch('')}><X size={13} className="text-gray-400 hover:text-gray-600" /></button>}
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border text-sm font-medium disabled:opacity-50"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface, color: SYSTEM_COLORS.textMuted }}>
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* Desktop table */}
      <div className="hidden overflow-hidden border lg:block rounded-2xl"
        style={{ borderColor: SYSTEM_COLORS.borderSoft, boxShadow: SYSTEM_COLORS.shadowSoft }}>
        <table className="min-w-full" style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}>
          <thead>
            <tr style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
              {['Role', 'Description', 'Permissions', 'Users', ''].map(h => (
                <th key={h} className={`px-5 py-3 text-[10px] font-semibold uppercase tracking-wider text-gray-500 ${h === '' ? 'text-right w-24' : 'text-left'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRoles.length === 0
              ? <tr><td colSpan={5} className="px-5 py-12 text-sm text-center text-gray-400">No roles found</td></tr>
              : filteredRoles.map((role, i) => (
              <tr key={role.id} className="transition-colors hover:bg-gray-50"
                style={{ borderTop: i > 0 ? `1px solid ${SYSTEM_COLORS.borderSoft}` : 'none' }}>
                <td className="px-5 py-3.5 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 text-sm font-bold rounded-full"
                      style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}>
                      {role.name?.charAt(0).toUpperCase() || 'R'}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{role.name}</p>
                      {role.isSystem && <p className="text-[10px] text-gray-400">System Role</p>}
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3.5 max-w-xs">
                  <p className="text-sm text-gray-500 truncate">{role.description}</p>
                </td>
                <td className="px-5 py-3.5 whitespace-nowrap">
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                    style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}>
                    {permCount(role)} enabled
                  </span>
                </td>
                <td className="px-5 py-3.5 whitespace-nowrap">
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                    style={{ backgroundColor: SYSTEM_COLORS.successTint, color: SYSTEM_COLORS.success }}>
                    {usersForRole(role)} users
                  </span>
                </td>
                <td className="px-5 py-3.5 whitespace-nowrap text-right">
                  <button onClick={() => setEditRole(role)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                    style={{ color: BRAND_COLORS.primary, backgroundColor: SYSTEM_COLORS.primaryTint }}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="space-y-3 lg:hidden">
        {filteredRoles.length === 0
          ? <div className="py-10 text-sm text-center text-gray-400">No roles found</div>
          : filteredRoles.map(role => (
          <div key={role.id} className="p-4 border rounded-2xl"
            style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 text-sm font-bold rounded-full"
                  style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}>
                  {role.name?.charAt(0).toUpperCase() || 'R'}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{role.name}</p>
                  {role.isSystem && <p className="text-[10px] text-gray-400">System Role</p>}
                </div>
              </div>
              <button onClick={() => setEditRole(role)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold flex-shrink-0"
                style={{ color: BRAND_COLORS.primary, backgroundColor: SYSTEM_COLORS.primaryTint }}>
                Edit
              </button>
            </div>
            {role.description && <p className="mt-2 text-xs text-gray-500">{role.description}</p>}
            <div className="flex gap-2 mt-3">
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}>
                {permCount(role)} permissions
              </span>
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                style={{ backgroundColor: SYSTEM_COLORS.successTint, color: SYSTEM_COLORS.success }}>
                {usersForRole(role)} users
              </span>
            </div>
          </div>
        ))}
      </div>

      {editRole && (
        <EditPermissionsModal
          role={editRole}
          permissionsByPage={permsByPage}
          usersForRole={usersForRole(editRole)}
          onClose={() => setEditRole(null)}
          onSaved={handlePermSaved}
        />
      )}
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const UserManagementPage = () => {
  const { hasPermission, user } = useApiAuth();
  const location = useLocation();

  const canManage     = hasPermission('admin.managePermissions');
  const canApprove    = hasPermission('admin.approveMembers');
  const isAdmin       = user?.role === 'admin';

  const tabs = [
    canManage  && { key: 'users',       label: 'User Management',  icon: Users     },
    canApprove && { key: 'approvals',   label: 'Member Approvals', icon: UserCheck },
    canManage  && { key: 'permissions', label: 'Permissions',      icon: Shield    },
  ].filter(Boolean);

  const urlTab = new URLSearchParams(location.search).get('tab');
  const [activeTab, setActiveTab] = useState(() =>
    (urlTab && tabs.find(t => t.key === urlTab)) ? urlTab : (tabs[0]?.key || 'users')
  );

  if (tabs.length === 0) return <Navigate to="/unauthorized" replace />;

  return (
    <div>
      {/* Tab bar */}
      <div className="border-b" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
        <nav className="flex -mb-px overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className="flex items-center gap-1.5 px-5 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors"
                style={{
                  borderColor: active ? BRAND_COLORS.primary : 'transparent',
                  color: active ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted,
                }}>
                <Icon size={15} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="pt-6">
        {activeTab === 'users'       && <UsersTab isAdmin={isAdmin} />}
        {activeTab === 'approvals'   && <ApprovalsTab />}
        {activeTab === 'permissions' && <PermissionsTab />}
      </div>
    </div>
  );
};

export default UserManagementPage;
